import type {BackupCode} from '@app/common/api'
import type {FormSubmissionStatus, FormType} from '@app/common/constants'
import {DEVELOPER} from '@app/common/constants'
import {CountryCode} from '@app/common/countries'
import {DEFAULT_STATUS_VALUE} from '@app/components/Modal/AdminRequestsModal'
import {api} from '@app/hooks/useApi'
import type {DefaultValues} from 'react-hook-form'
import {devtools, persist} from 'zustand/middleware'
import {createWithEqualityFn as create} from 'zustand/traditional'

type OverlayStore = {
  adminApplicationId: string | null
  adminAuditLogOpen: boolean
  adminExperimentsOpen: boolean
  adminPlayer: string | null
  adminPlayerWebIp: string | null
  adminRequestsOffenderXuid: string[]
  adminRequestsOpen: boolean
  adminRequestsPlayerXuid: string[]
  adminRequestsReviewedBy: string[]
  adminRequestsStatus: FormSubmissionStatus[]
  adminRequestsType: FormType[]
  adminServerAnnouncementsOpen: boolean
  adminServerSettingsOpen: boolean
  adminServerUpdatesOpen: boolean
  adminSystemMessagePlayer: string | null
  adminTournamentOpen: boolean
  adminGiveCosmeticsOpen: boolean
  applicationId: string | null
  externalLinkUrl: string | null
  mfaBackupCodes: BackupCode[]
  mfaEnableForceOpen: boolean
  mfaEnableOpen: boolean
  mfaVerifyOpen: boolean
  quickSearchOpen: boolean
  tierSummaryOpen: boolean
  requestId: string | null
  requestsOpen: boolean
  settingsOpen: boolean
  splashScreenOpen: boolean
  staffDirectoryOpen: boolean
  tournamentDetails: boolean
  youtubeDirectoryOpen: boolean
  setAdminApplicationId(value: string | null): void
  setAdminAuditLogOpen(value: boolean): void
  setAdminExperimentsOpen(value: boolean): void
  setAdminPlayer(value: string | null): void
  setAdminPlayerWebIp(value: string | null): void
  setAdminRequestsOffenderXuid(value: string[]): void
  setAdminRequestsOpen(value: boolean): void
  setAdminRequestsPlayerXuid(value: string[]): void
  setAdminRequestsReviewedBy(value: string[]): void
  setAdminRequestsStatus(value: FormSubmissionStatus[]): void
  setAdminRequestsType(value: FormType[]): void
  setAdminServerAnnouncementsOpen(value: boolean): void
  setAdminServerSettingsOpen(value: boolean): void
  setAdminServerUpdatesOpen(value: boolean): void
  setAdminSystemMessagePlayer(value: string | null): void
  setAdminTournamentOpen(value: boolean): void
  setAdminGiveCosmeticsOpen(value: boolean): void
  setApplicationId(value: string | null): void
  setExternalLinkUrl(value: string | null): void
  setMfaBackupCodes(value: BackupCode[]): void
  setMfaEnableOpen(value: boolean, force?: boolean): void
  setMfaVerifyOpen(value: boolean): void
  setQuickSearchOpen(value: boolean): void
  setTierSummaryOpen(value: boolean): void
  setRequestId(value: string | null): void
  setRequestsOpen(value: boolean): void
  setSettingsOpen(value: boolean): void
  setSplashScreenOpen(value: boolean): void
  setStaffDirectoryOpen(value: boolean): void
  setTournamentDetails(value: boolean): void
  setYoutubeDirectoryOpen(value: boolean): void
}

export const useOverlayStore = create<OverlayStore>()(
  devtools(
    set => ({
      adminApplicationId: null,
      adminAuditLogOpen: false,
      adminExperimentsOpen: false,
      adminPlayer: null,
      adminPlayerWebIp: null,
      adminRequestsOffenderXuid: [],
      adminRequestsOpen: false,
      adminRequestsPlayerXuid: [],
      adminRequestsReviewedBy: [],
      adminRequestsStatus: DEFAULT_STATUS_VALUE,
      adminRequestsType: [],
      adminServerAnnouncementsOpen: false,
      adminServerSettingsOpen: false,
      adminServerUpdatesOpen: false,
      adminSystemMessagePlayer: null,
      adminTournamentOpen: false,
      adminGiveCosmeticsOpen: false,
      applicationId: null,
      externalLinkUrl: null,
      mfaBackupCodes: [],
      mfaEnableForceOpen: false,
      mfaEnableOpen: false,
      mfaVerifyOpen: false,
      quickSearchOpen: false,
      tierSummaryOpen: false,
      requestId: null,
      requestsOpen: false,
      settingsOpen: false,
      splashScreenOpen: false,
      staffDirectoryOpen: false,
      tournamentDetails: false,
      youtubeDirectoryOpen: false,
      setAdminApplicationId: value => set({adminApplicationId: value}),
      setAdminAuditLogOpen: value => set({adminAuditLogOpen: value}),
      setAdminExperimentsOpen: value => set({adminExperimentsOpen: value}),
      setAdminPlayer: value => set({adminPlayer: value}),
      setAdminPlayerWebIp: value => set({adminPlayerWebIp: value}),
      setAdminRequestsOffenderXuid: value => set({adminRequestsOffenderXuid: value}),
      setAdminRequestsOpen: value => set({adminRequestsOpen: value}),
      setAdminRequestsPlayerXuid: value => set({adminRequestsPlayerXuid: value}),
      setAdminRequestsReviewedBy: value => set({adminRequestsReviewedBy: value}),
      setAdminRequestsStatus: value => set({adminRequestsStatus: value}),
      setAdminRequestsType: value => set({adminRequestsType: value}),
      setAdminServerAnnouncementsOpen: value => set({adminServerAnnouncementsOpen: value}),
      setAdminServerSettingsOpen: value => set({adminServerSettingsOpen: value}),
      setAdminServerUpdatesOpen: value => set({adminServerUpdatesOpen: value}),
      setAdminSystemMessagePlayer: value => set({adminSystemMessagePlayer: value}),
      setAdminTournamentOpen: value => set({adminTournamentOpen: value}),
      setAdminGiveCosmeticsOpen: value => set({adminGiveCosmeticsOpen: value}),
      setApplicationId: value => set({applicationId: value}),
      setExternalLinkUrl: value => set({externalLinkUrl: value}),
      setMfaBackupCodes: backupCodes => set({mfaBackupCodes: backupCodes}),
      setMfaEnableOpen: (value, force = false) => set({mfaEnableOpen: value, mfaEnableForceOpen: force}),
      setMfaVerifyOpen: value => set({mfaVerifyOpen: value}),
      setQuickSearchOpen: value => set({quickSearchOpen: value}),
      setTierSummaryOpen: value => set({tierSummaryOpen: value}),
      setRequestId: value => set({requestId: value}),
      setRequestsOpen: value => set({requestsOpen: value}),
      setSettingsOpen: value => set({settingsOpen: value}),
      setSplashScreenOpen: value => set({splashScreenOpen: value}),
      setStaffDirectoryOpen: value => set({staffDirectoryOpen: value}),
      setTournamentDetails: value => set({tournamentDetails: value}),
      setYoutubeDirectoryOpen: value => set({youtubeDirectoryOpen: value}),
    }),
    {name: 'OverlayStore'},
  ),
)

type SettingsStore = {
  bypassAnonymous: boolean
  bypassFormRequirements: boolean
  developerMode: boolean
  reducedMotion: boolean
  renderSkins: boolean
  skinProxyUrl: string | null
  syncReducedMotion: boolean
  setBypassAnonymous(value: boolean): void
  setBypassFormRequirements(value: boolean): void
  setDeveloperMode(value: boolean): void
  setReducedMotion(value: boolean): void
  setRenderSkins(value: boolean): void
  setSkinProxyUrl(value: string): void
  setSyncReducedMotion(value: boolean): void
}

export const useSettingsStore = create<SettingsStore>()(
  devtools(
    persist(
      set => ({
        bypassAnonymous: false,
        bypassFormRequirements: false,
        developerMode: DEVELOPER,
        reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        renderSkins: true,
        skinProxyUrl: null,
        syncReducedMotion: true,
        setBypassAnonymous: value => set({bypassAnonymous: value}),
        setBypassFormRequirements: value => set({bypassFormRequirements: value}),
        setDeveloperMode: value => set({developerMode: value}),
        setReducedMotion: value => set({reducedMotion: value}),
        setRenderSkins: value => set({renderSkins: value}),
        setSkinProxyUrl: value => set({skinProxyUrl: value}),
        setSyncReducedMotion: value => set({syncReducedMotion: value}),
      }),
      {name: 'SettingsStore', version: 1},
    ),
  ),
)

type DraftStore = {
  drafts: Partial<Record<FormType, DefaultValues<any>>>
  setDraft(form: FormType, values: DefaultValues<any>): void
  reset(): void
}

export const useDraftStore = create<DraftStore>()(
  devtools(
    persist(
      set => ({
        drafts: {},
        setDraft: (form, values) => set(state => ({drafts: {...state.drafts, [form]: values}})),
        reset: () => set({drafts: {}}),
      }),
      {name: 'DraftStore', version: 1},
    ),
  ),
)

type CountryCodeStore = {
  countryCode: CountryCode
  setCountryCode(countryCode: CountryCode): void
  loadCountryCode(): Promise<void>
}

export const useCountryCodeStore = create<CountryCodeStore>()(
  devtools(
    persist(
      set => ({
        countryCode: CountryCode.US,
        setCountryCode: (countryCode: CountryCode) => set({countryCode}),
        loadCountryCode: async () => {},
      }),
      {name: 'CountryCodeStore', version: 1},
    ),
  ),
)

type DelayedFormsStore = {
  delayedForms: Partial<Record<FormType, boolean>>
  isFormDelayed(form: FormType): boolean
  loadDelayedForms(): Promise<void>
}

export const useDelayedFormsStore = create<DelayedFormsStore>()(
  devtools(
    persist(
      (set, get) => ({
        delayedForms: {},
        isFormDelayed: (form: FormType) => form in get().delayedForms && get().delayedForms[form] === true,
        loadDelayedForms: async () => {
          const {data} = await api.get<Record<FormType, boolean>>('delayed-forms')
          set({delayedForms: data})
        },
      }),
      {name: 'DelayedFormsStore', version: 1},
    ),
  ),
)
